<template>
  <div>

    <table-tools :actions="tableActions" />

    <v-data-table
      :items="parcs"
      :headers="headers"
      :sort-by="sortBy"
      :loading="loadingParcs"
    >

      <template v-slot:item.companyName="{ item }">
        {{ getEnumLabel(item.companyName, ParcsCompanies) }}
      </template>

      <template v-slot:item.channels="{ item }">
        <template v-if="!item.channels.length">
          {{ $t('misc.none') }}
        </template>
        <template v-else>
          <template v-for="(channel, i) in item.channels.toSorted()">
            <template v-if="i > 0">, </template>
            {{ ChannelRegistry.find(channel)?.titleShort }}
          </template>
        </template>
      </template>

      <template v-slot:item.live="{ item }">
        {{ item.live ? $t('misc.yes') : $t('misc.no') }}
      </template>

      <template v-slot:item.actions="{ item }">

        <action-group
          :actions="actions"
          @edit-clicked="editParcs(item)"
          @delete-clicked="deleteParcs(item)"
        />

      </template>

    </v-data-table>

  </div>
</template>

<script setup lang="ts">

import { computed, inject, ref } from 'vue'
import { useI18n } from 'vue-i18n'
import { useTitle } from '@vueuse/core'

// components
import ParcsModal from './ParcsModal.vue'
import ParcsDeleteModal from './ParcsDeleteModal.vue'

// models
import Location from '../../../../models/Location'
import Parcs from '../../../../models/Parcs'

// enums
import { ParcsCompanies } from '../../../../enums'

// misc
import getEnumLabel from '../../../../utils/getEnumLabel'
// @ts-ignore
import { openDialog } from 'vue3-promise-dialog'
import ChannelRegistry from '../../../../channels/ChannelRegistry'
import NotificationService from '../../../../services/NotificationService'
import type { ActionGroupAction } from '../../../../types/misc'
import type { Can } from '../../../../main'
import type { VDataTableHeader } from '../../../../types/vuetify'

interface Props {
  location: Location
}

const props = defineProps<Props>()

const { t } = useI18n()
const title = useTitle()
title.value = t('titles.location_parcs', {location_name: props.location.locationName})

const $can = inject('$can') as Can
const canSeeIds = $can('seeids')
const canWriteLocations = $can('locations.write')

const actions: ActionGroupAction[] = []

if (canWriteLocations) {
  actions.push({ label: t('actions.edit'), icon: '$edit', event: 'editClicked' })
  actions.push({ label: t('actions.delete'), icon: '$delete', event: 'deleteClicked' })
} else {
  actions.push({ label: t('actions.view'), icon: '$view', event: 'editClicked' })
}

const parcs = computed(() => {
  return Parcs.query().where('locationID', props.location.ID).get()
})

const tableActions = computed(() => [

  {
    title: t('actions.reload'),
    icon: '$reload',
    click: loadParcs,
    busy: loadingParcs.value,
  },
  {
    title: t('actions.create_parcs'),
    icon: '$create',
    click: createParcs,
    hidden: !canWriteLocations,
  },
])

/**
 * Data table configs
 */

const headers: VDataTableHeader[] = []

if (canSeeIds) {
  headers.push({ title: t('titles.id'), key: 'ID' })
}

headers.push({ title: t('titles.company'), key: 'companyName' })
headers.push({ title: t('titles.external_id'), key: 'externalID' })
headers.push({ title: t('titles.channels'), key: 'channels' })
headers.push({ title: t('misc.live'), key: 'live' })
headers.push({ title: '', key: 'actions', sortable: false })

const sortBy = ref([{ key: 'companyName' }])

/**
 * Initialization
 */

const loadingParcs = ref<boolean>(false)

loadParcs()

function loadParcs() {

  loadingParcs.value = true

  Parcs.fetchByLocationID(props.location.ID)
    .catch((error) => {
      console.error(error)
      NotificationService.error(t('alerts.couldnt_load_parcs'))
    })
    .finally(() => {
      loadingParcs.value = false
    })

}

/**
 * Modals
 */

function createParcs() {
  const parcs = new Parcs()
  parcs.locationID = props.location.ID
  openDialog(ParcsModal, { parcs: parcs })
}

function editParcs(parcs: Parcs) {
  openDialog(ParcsModal, { parcs: parcs })
}

function deleteParcs(parcs: Parcs) {
  openDialog(ParcsDeleteModal, { parcs: parcs })
}

</script>
